import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function Footer() {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white relative">
      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-20 sm:bottom-24 right-4 sm:right-6 w-11 h-11 sm:w-12 sm:h-12 bg-amber-500 hover:bg-amber-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 z-40 cursor-pointer"
          aria-label="Back to top"
        >
          <i className="ri-arrow-up-line text-xl sm:text-2xl"></i>
        </button>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12 mb-8 sm:mb-12">
          {/* About Column */}
          <div>
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/abcfde42a70d25526c2b82811ba21d87/cf412d9fb78223cea549cff9d81f857f.png"
                  alt="PWC Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <div className="font-serif font-bold text-base sm:text-lg leading-tight">
                  Praying Women Connect
                </div>
                <div className="text-xs text-amber-400">
                  Connecting With Jesus
                </div>
              </div>
            </div>
            <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
              A global community of prayer, purpose, and power uniting women across nations.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-serif text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-amber-400">Quick Links</h3>
            <ul className="space-y-2 sm:space-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'About Us', path: '/about' },
                { name: 'Programs', path: '/programs' },
                { name: 'Testimonies', path: '/testimonies' },
                { name: 'Gallery', path: '/gallery' },
                { name: 'The Quist Foundation', path: '/foundation' },
                { name: 'Contact', path: '/contact' },
                { name: 'Prayer Request', path: '/prayer-request' }
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-300 hover:text-amber-400 transition-colors duration-300 cursor-pointer text-sm sm:text-base inline-block py-1"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* The Quist Foundation */}
          <div>
            <h3 className="font-serif text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-amber-400">The Quist Foundation</h3>
            <p className="text-gray-300 text-sm sm:text-base mb-4">
              Healthcare philanthropy and community development across Ghana.
            </p>
            <div className="space-y-3">
              <a
                href="https://instagram.com/thequistfoundation"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gray-300 hover:text-amber-400 transition-colors duration-300 cursor-pointer text-sm sm:text-base py-1"
              >
                <i className="ri-instagram-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span>@thequistfoundation</span>
              </a>
              <a
                href="https://tiktok.com/@thequistfoundation"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-gray-300 hover:text-amber-400 transition-colors duration-300 cursor-pointer text-sm sm:text-base py-1"
              >
                <i className="ri-tiktok-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span>@thequistfoundation</span>
              </a>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-serif text-lg sm:text-xl font-bold mb-4 sm:mb-6 text-amber-400">Contact Us</h3>
            <ul className="space-y-3 sm:space-y-4">
              <li className="flex items-start gap-3">
                <i className="ri-phone-line text-amber-400 text-lg sm:text-xl w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                <div className="text-gray-300 text-sm sm:text-base">
                  <div>+44 7983 627899</div>
                  <div>+233 54 267 2880</div>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-instagram-line text-amber-400 text-lg sm:text-xl w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                <div className="text-gray-300 text-sm sm:text-base break-words">
                  @prayingwomenconnectglobal
                </div>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-tiktok-line text-amber-400 text-lg sm:text-xl w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0 mt-0.5"></i>
                <div className="text-gray-300 text-sm sm:text-base break-words">
                  @prayingwomenconnectglobal
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Social Media */}
        <div className="border-t border-gray-700 pt-6 sm:pt-8 mb-6 sm:mb-8">
          <div className="flex justify-center gap-4 sm:gap-6">
            <a
              href="https://instagram.com/prayingwomenconnectglobal"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-amber-500 transition-all duration-300 cursor-pointer"
              aria-label="Instagram"
            >
              <i className="ri-instagram-line text-xl sm:text-2xl"></i>
            </a>
            <a
              href="https://tiktok.com/@prayingwomenconnectglobal"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-amber-500 transition-all duration-300 cursor-pointer"
              aria-label="TikTok"
            >
              <i className="ri-tiktok-line text-xl sm:text-2xl"></i>
            </a>
            <a
              href="https://wa.me/447983627899"
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-amber-500 transition-all duration-300 cursor-pointer"
              aria-label="WhatsApp"
            >
              <i className="ri-whatsapp-line text-xl sm:text-2xl"></i>
            </a>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center text-gray-400 text-xs sm:text-sm">
          <p>&copy; {new Date().getFullYear()} Praying Women Connect Global. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}